import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import classNames from 'classnames'
import { debounce, isFunction } from 'lodash'
import { withRouter } from 'react-router-dom'

import ModalButton from './ModalButton'
import Button from 'components/UI/Button'
import AppContext from 'components/AppContext'
import ModalMobileHeader from 'components/Modal/ModalMobileHeader/ModalMobileHeader'

import './generic-modal.pcss'

export const GenericModalContext = React.createContext()

export const MODAL_SIZE = Object.freeze({
    LARGE: 'large',
    SMALL: 'small'
})

class GenericModal extends Component {
    mounted = false

    constructor (props) {
        super(props)

        this.modal = React.createRef()
        this.alignTopUpdateInterval = null

        this.state = {
            isOpen: true,
            alignToTop: false,
            title: props.initialTitle,
        }

        this.handleResize = debounce(this.handleResize.bind(this), 100)
    }

    componentDidMount () {
        this.mounted = true

        window.addEventListener('resize', this.handleResize)
        this.handleResize()
        this.alignTopUpdateInterval = setInterval(this.updateAlignTop, 100)
    }

    componentWillUnmount () {
        this.mounted = false

        clearInterval(this.alignTopUpdateInterval)
        window.removeEventListener('resize', this.handleResize)
    }

    componentDidUpdate (prevProps) {
        if (this.props.closeOnRouteChange) {
            this.handleCloseOnRouteChange(prevProps)
        }
    }

    handleCloseOnRouteChange (prevProps) {
        const {
            location,
        } = this.props

        const {
            location: prevLocation,
        } = prevProps

        if (location.key !== prevLocation.key) {
            this.close()
        }
    }

    close = () => {
        this.setState({ isOpen: false })

        const {
            onClose,
            name,
        } = this.props

        onClose && onClose(name)
    }

    open = () => {
        this.setState({ isOpen: true }, () => this.handleResize())
    }

    setTitle = (title) => {
        this.setState({ title })
    }

    renderButtons = (buttons) => {
        return buttons && buttons.length > 0 && (
            <ul className="react-modal__buttons">
                {buttons.map(function (button, key) {
                    return <li key={key}>{ button }</li>
                })}
            </ul>
        )
    }

    actions = {
        openModal: this.open,
        closeModal: this.close,
        renderModalButtons: this.renderButtons,
        setTitle: this.setTitle,
    }

    render () {
        const {
            name,
            size,
            children,
            okButtonTxt,
            okButtonClasses,
            cancelButtonTxt,
            cancelButtonClasses,
            onAfterOpen,
            onRequestClose,
            buttons,
            requireUserInput,
            footer,
            context,
        } = this.props

        const {
            isOpen,
            alignToTop,
            title,
        } = this.state

        const closeRequest = onRequestClose || this.handleCloseRequest

        const okButton = okButtonTxt
            ? CreateButton(okButtonTxt, this.handleOkClick, ['positive', okButtonClasses]) : null
        const cancelButton = cancelButtonTxt
            ? CreateButton(cancelButtonTxt, this.handleCancelClick, ['negative', cancelButtonClasses]) : null

        const allButtons = buttons ? [...buttons] : []

        if (cancelButton) allButtons.unshift(cancelButton)
        if (okButton) allButtons.unshift(okButton)

        return (
            <AppContext context={context}>
                <ModalMobileHeader
                    requireUserInput={requireUserInput}
                    onClose={this.handleCloseButtonClick}
                    buttons={allButtons}
                />
                {/* container to render map via ReactDOM.createPortal to break out of the modal css positioning */}
                <div id="maps-place-select-container" />
                <Modal
                    ariaHideApp={false}
                    isOpen={isOpen}
                    onAfterOpen={onAfterOpen}
                    onRequestClose={closeRequest}
                    contentLabel="Modal"
                    ref={ref => { this.modal = ref }}
                    className={{
                        base: classNames('react-modal animated fadeInDown fastest', size ? `react-modal--size-${size}` : '', name),
                        afterOpen: 'afterOpen',
                        beforeClose: 'beforeClose',
                    }}
                    overlayClassName={{
                        base: classNames(
                            'react-modal-overlay animated fadeIn fastest', name, { alignToTop: alignToTop }
                        ),
                        afterOpen: 'afterOpen',
                        beforeClose: 'beforeClose',
                    }}
                >
                    {!requireUserInput && this.closeButtonElement('react-modal__close-button')}
                    <div className="react-modal__wrapper">
                        {title && <h1 className="react-modal__modal-title">{title}</h1>}
                        {children && this.renderChildren()}
                        {this.renderButtons(allButtons)}
                        {footer && <div className="react-modal__modal-footer">{footer}</div>}
                    </div>
                </Modal>
            </AppContext>
        )
    }

    renderChildren = () => {
        const { children } = this.props

        const modalState = {
            ...this.state,
            ...this.actions,
            mobileButtonsDOMElement: document.getElementById('mobile-modal-buttons'),
        }

        return (
            <GenericModalContext.Provider value={modalState}>
                <div className="react-modal__modal-content">
                    {isFunction(children) ? children(modalState) : children}
                </div>
            </GenericModalContext.Provider>
        )
    }

    closeButtonElement (className) {
        return (
            <Button
                trackName="closeModal"
                className={className}
                onClick={this.handleCloseButtonClick}
            >
                <span />
            </Button>
        )
    }

    handleCloseButtonClick = () => {
        this.close()
    }

    handleCloseRequest = () => {
        if (!this.props.requireUserInput && !this.props.disableBackdropCloseClick) {
            this.close()
        }
    }

    handleOkClick = () => {
        const { okClickCloses, onOkClick } = this.props

        if (okClickCloses === true) {
            this.close()
        }

        if (onOkClick) {
            onOkClick()
        }
    }

    handleCancelClick = () => {
        const { cancelClickCloses, onCancelClick } = this.props

        if (cancelClickCloses === true) {
            this.close()
        }

        if (onCancelClick) {
            onCancelClick()
        }
    }

    handleResize = () => {
        if (this.modal && this.mounted) {
            this.updateAlignTop()
        }
    }

    updateAlignTop = () => {
        const windowHeight = window.innerHeight
        const margin = 100
        const contentHeight = this.modal.portal.content ? this.modal.portal.content.clientHeight : 0
        const modalHeight = contentHeight + margin
        const alignToTop = windowHeight < modalHeight

        if (this.state.alignToTop !== alignToTop) {
            this.setState({
                alignToTop,
            })
        }
    }
}

export function CreateButton (text, callback, classes, ...args) {
    return (
        <ModalButton
            text={text}
            callback={callback}
            classes={classes}
            {...args}
        />
    )
}

GenericModal.propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.any.isRequired,
    context: PropTypes.string.isRequired,
    initialTitle: PropTypes.string,
    okButtonTxt: PropTypes.string,
    okButtonClasses: PropTypes.string,
    cancelButtonTxt: PropTypes.string,
    cancelButtonClasses: PropTypes.string,
    onAfterOpen: PropTypes.func,
    onClose: PropTypes.func,
    onRequestClose: PropTypes.func,
    buttons: PropTypes.array,
    requireUserInput: PropTypes.bool,
    closeOnRouteChange: PropTypes.bool,
    disableBackdropCloseClick: PropTypes.bool,
    footer: PropTypes.node,
}

GenericModal.defaultProps = {
    closeOnRouteChange: false,
    okClickCloses: true,
    cancelClickCloses: true,
    requireUserInput: true,
    disableBackdropCloseClick: false,
}

export default withRouter(GenericModal)
